/* eslint-disable no-lonely-if */
import { Box, Flex, useMediaQuery } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import 'matchmedia-polyfill';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import AuthModal from 'components/AuthModal';
import FullScreenLoader from 'components/FullScreenLoader';
import HideIconComponent from 'components/Icons/HideIcon';
import ShowIconComponent from 'components/Icons/ShowIcon';
import NavigationHeader from 'pages/NavigationHeader';
import { HEADER_HEIGHT_WITH_NAVIGATION, STATUS } from 'projectConstants';
import { resetDimension } from 'redux/DimensionPageSlice';
import { resetIndicator } from 'redux/IndicatorPageSlice';
import { resetItem } from 'redux/ItemPageSlice';
import { GetUserModels } from 'redux/ModelsSlice';
import {
  ShowSidePanel,
  blockDetailStatus,
  blockDriversStatus,
  blockMetadataStatus,
  FetchBlockItemDimensions,
  fetchBlock,
  fetchBlockDrivers,
  fetchBlockMetadata,
  fetchModel,
  // fetchTableData,
  getAppliedFilters,
  getBlockDetails,
  getBlockDimensions,
  getBlockDriversData,
  getBlockMetadata,
  getModelDetails,
  handleSidePanel,
  inputsPageShow,
  modelDetailStatus,
  overallGroupByFilter,
  overallIndicatorFilter,
  overallTimeFilter,
  planPageShow,
  reset,
  setDriver,
  showAuthModel,
  toggleAuthModel,
  filterStartDate,
  filterEndDate,
  groupByFilterChange,
  handleFilters,
  setSelectedBlockDimensionDirect,
} from 'redux/PlanPageSlice';
import {
  fetchAllScenarios,
  fetchScenarioStatus,
  getAllScenarios,
  getBaseScenario,
  getCompareScenario,
  resetScenario,
  selectBaseScenario,
  updateModelandBlock,
} from 'redux/ScenarioSlice';
import { GetInputAdjustments, resetDriverAdjustmentSlider } from 'redux/driverAdjustmentSlice';
import store from 'redux/store/store';
import { useBloxBlockPageTitle } from 'hooks/useBloxDocumentTitle';
import { AppDispatch, getClosestToTargetItemCountDimension } from 'utils/GlobalHelpers';

import {
  resetPlanPageRefetchScheduler,
  schedulePlanPageRefetch,
} from './PlanPageUIRevamp/planPageFetchHelpers';
import {
  mergeSavedFiltersWithBlockDimensions,
  readSavedPlanBlockFilters,
} from './PlanPageUIRevamp/planPageFilterHydration';
import InputSection from './Inputs/InputsSection';
import MobileLayout from './MobileLayout';
import PlanSection from './Plan/planSection';
import PlanPageHeader from './PlanPageHeader';
import DriversSection from './driversSection';

const PlanPageScenario = () => {
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const showPlanPage = useSelector(planPageShow);
  const metadata = useSelector(getBlockMetadata);
  const showSidePanel = useSelector(ShowSidePanel);
  const baseScenario = useSelector(getBaseScenario);
  const showInputPage = useSelector(inputsPageShow);
  const isAuthModalOpen = useSelector(showAuthModel);
  const modelStatus = useSelector(modelDetailStatus);
  const blockStatus = useSelector(blockDetailStatus);
  const selectedYear = useSelector(overallTimeFilter);
  const blockDrivers = useSelector(getBlockDriversData);
  const driversStatus = useSelector(blockDriversStatus);
  const metadataStatus = useSelector(blockMetadataStatus);
  const compareScenario = useSelector(getCompareScenario);
  const inputAdjustments = useSelector(GetInputAdjustments);
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const allScenarioStatus = useSelector(fetchScenarioStatus);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const scenarios = useSelector(getAllScenarios);
  const appliedFilters = useSelector(getAppliedFilters);
  const modelDetail = useSelector(getModelDetails);
  const blockDetail = useSelector(getBlockDetails);
  const blockDimensions = useSelector(getBlockDimensions);
  const shouldForceRefetchAfterHydration = useRef(false);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [isTablet] = useMediaQuery('(max-width: 768px)');
  const [isLaptop] = useMediaQuery('(max-width: 1024px)');
  const startDatePlanPage = useSelector(filterStartDate);
  const endDatePlanPage = useSelector(filterEndDate);
  const dateFilterSelected = startDatePlanPage && endDatePlanPage;
  const dateFilter = `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`;

  useBloxBlockPageTitle(blockDetail?.name, modelDetail?.name, 'Plan');

  const isLoading =
    modelStatus === STATUS.LOADING ||
    blockStatus === STATUS.LOADING ||
    driversStatus === STATUS.LOADING ||
    metadataStatus === STATUS.LOADING ||
    allScenarioStatus === STATUS.LOADING;

  const initialDataLoad =
    metadataStatus === STATUS.LOADING ||
    allScenarioStatus === STATUS.LOADING ||
    modelStatus === STATUS.LOADING ||
    blockStatus === STATUS.LOADING;

  const blockDimensionsReady =
    blockDimensions != null &&
    !Array.isArray(blockDimensions) &&
    Array.isArray(blockDimensions.dimensions);

  const canFetchData =
    Boolean(blockId && baseScenario?.id && metadata && selectedGroupBy !== null && selectedYear) &&
    !isLoading;

  const onToggleAuthModal = () => dispatch(toggleAuthModel());

  useEffect(() => {
    // Always prefer modalFilters.scenario if present and valid
    if (modelId) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      if (modalFilters?.scenario) {
        // Try to find the scenario by id (if it's an object or just an id)
        let scenarioToSelect = null;
        scenarioToSelect =
          typeof modalFilters.scenario === 'object' && modalFilters.scenario.id
            ? scenarios.find((s: any) => s.id === modalFilters.scenario.id)
            : scenarios.find((s: any) => s.id === modalFilters.scenario);
        if (scenarioToSelect) {
          dispatch(selectBaseScenario(scenarioToSelect));
        }
      } else if (baseScenario) {
        // Fallback to baseScenario from redux if present in scenarios
        const base = scenarios.find((scenario: any) => scenario.id === baseScenario.id);
        if (base) {
          dispatch(selectBaseScenario(base));
        } else if (scenarios?.length > 0) {
          const baseSec = scenarios.find((scenario: any) => scenario.is_base);
          dispatch(selectBaseScenario(baseSec));
        }
      } else {
        // Fallback to first base scenario in list
        const baseSec = scenarios.find((scenario: any) => scenario.is_base);
        dispatch(selectBaseScenario(baseSec));
      }

      // Gloabl Plan Page Group By Filter sync
      if (metadata && blockId) {
        const blockGroupBy = modalFilters?.[blockId]?.groupBy
          ? metadata.dimensions.find((dim: any) => dim.id === modalFilters?.[blockId]?.groupBy)
          : null;
        const groupBy = blockGroupBy
          ? blockGroupBy.id
          : metadata.dimensions && metadata.dimensions.length > 0
          ? getClosestToTargetItemCountDimension(metadata?.dimensions)
          : null;
        dispatch(groupByFilterChange(groupBy || 'Total'));
      }
    } else {
      // If no modelId, fallback to base scenario logic
      if (baseScenario) {
        const base = scenarios.find((scenario: any) => scenario.id === baseScenario.id);
        if (base) {
          dispatch(selectBaseScenario(base));
        } else if (scenarios?.length > 0) {
          const baseSec = scenarios.find((scenario: any) => scenario.is_base);
          if (baseSec) {
            dispatch(selectBaseScenario(baseSec));
          }
        }
      } else if (scenarios?.length > 0) {
        const baseSec = scenarios.find((scenario: any) => scenario.is_base);
        if (baseSec) {
          dispatch(selectBaseScenario(baseSec));
        }
      }
    }
  }, [scenarios, baseScenario, modelId, metadata]);

  useLayoutEffect(() => {
    resetPlanPageRefetchScheduler();
    shouldForceRefetchAfterHydration.current = false;

    if (!blockId || !modelId) {
      setFiltersHydrated(false);
      return;
    }

    const savedFilters = readSavedPlanBlockFilters(blockDetail?.model_id ?? modelId, blockId);
    if (savedFilters?.selectedBlockDimension?.length) {
      dispatch(setSelectedBlockDimensionDirect(savedFilters.selectedBlockDimension));
      dispatch(handleFilters());
      shouldForceRefetchAfterHydration.current = true;
    }

    setFiltersHydrated(true);
  }, [blockDetail?.model_id, blockId, dispatch, modelId]);

  useEffect(() => {
    if (!blockId || !modelId || !blockDimensionsReady) return;

    const savedFilters = readSavedPlanBlockFilters(blockDetail?.model_id ?? modelId, blockId);
    if (!savedFilters?.selectedBlockDimension?.length) return;

    const mergedFilters = mergeSavedFiltersWithBlockDimensions(
      savedFilters.selectedBlockDimension,
      blockDimensions.dimensions,
    );
    dispatch(setSelectedBlockDimensionDirect(mergedFilters));
    dispatch(handleFilters());
  }, [
    blockDetail?.model_id,
    blockDimensions.dimensions,
    blockDimensionsReady,
    blockId,
    dispatch,
    modelId,
  ]);

  const refetchRequest = useCallback(
    (options?: { force?: boolean }) => {
      if (!canFetchData || !filtersHydrated) return;
      schedulePlanPageRefetch(dispatch, store.getState, blockId, options, modelId);
    },
    [blockId, canFetchData, dispatch, filtersHydrated, modelId],
  );

  const onTogglePanel = async () => {
    if (showInputPage) {
      dispatch(setDriver(null));
    }
    dispatch(handleSidePanel());
  };

  useEffect(() => {
    dispatch(GetUserModels({}));
  }, []);

  useEffect(() => {
    dispatch(reset());
    dispatch(resetScenario());
    dispatch(resetDriverAdjustmentSlider());
    dispatch(setDriver(null));
    dispatch(resetItem());
    dispatch(resetDimension());
    dispatch(resetIndicator());
    resetPlanPageRefetchScheduler();
  }, [pathname]);

  useEffect(() => {
    if (modelId && blockId) {
      dispatch(updateModelandBlock({ modelId, blockId }));
      dispatch(fetchModel(Number(modelId)));
      dispatch(fetchBlock(Number(blockId)));
      dispatch(fetchAllScenarios(Number(modelId)));
      dispatch(
        fetchBlockDrivers({
          blockId,
          parameters: {
            scenario_id: baseScenario?.id,
          },
        }),
      );
    }
  }, [pathname]);

  useEffect(() => {
    if (blockId && baseScenario?.id) {
      dispatch(
        fetchBlockMetadata({
          blockId: Number(blockId),
          parameters: { scenario_id: baseScenario?.id },
        }),
      );
      dispatch(
        FetchBlockItemDimensions({
          blockId,
          parameters: { params: { scenario_id: baseScenario.id } },
        }),
      );
    }
  }, [blockId, baseScenario?.id, dispatch]);

  useEffect(() => {
    if (baseScenario?.id) {
      dispatch(
        fetchBlockDrivers({
          blockId,
          parameters: {
            scenario_id: baseScenario?.id,
          },
        }),
      );
    }
  }, [!initialDataLoad]);

  useEffect(() => {
    if (!canFetchData || !filtersHydrated) return;
    if (!showPlanPage && !compareScenario) return;

    const force = shouldForceRefetchAfterHydration.current;
    shouldForceRefetchAfterHydration.current = false;
    refetchRequest(force ? { force: true } : undefined);
  }, [
    appliedFilters,
    baseScenario?.id,
    blockId,
    compareScenario?.id,
    dateFilter,
    dateFilterSelected,
    filtersHydrated,
    indicatorFilter,
    inputAdjustments,
    selectedGroupBy,
    selectedYear,
    canFetchData,
    showPlanPage,
    compareScenario,
    refetchRequest,
  ]);

  useEffect(() => {
    return () => {
      dispatch(reset());
      dispatch(resetScenario());
      dispatch(resetDriverAdjustmentSlider());
      dispatch(setDriver(null));
      dispatch(resetItem());
      dispatch(resetDimension());
      dispatch(resetIndicator());
    };
  }, []);

  if (isLoading) return <FullScreenLoader />;

  if (isLaptop) {
    return (
      <Box>
        <NavigationHeader />
        {blockDrivers?.length > 0 && <MobileLayout refetchRequest={refetchRequest} />}
        {blockDrivers?.length === 0 && <PlanSection refetchRequest={refetchRequest} />}
      </Box>
    );
  }

  return (
    <Box px={isTablet ? 4 : 7} py={4}>
      <>
        <NavigationHeader />
        <PlanPageHeader refetchRequest={refetchRequest} />
      </>
      {blockDrivers?.length > 0 ? (
        <Flex
          width={'100%'}
          className='scroll'
          h={`calc(100vh - ${HEADER_HEIGHT_WITH_NAVIGATION})`}
          borderTop={'2px #50489B solid'}
        >
          {/* Side Panel */}
          <Box position={'relative'} pr={showSidePanel ? 0 : 8}>
            {blockDrivers && (
              <Box position={'absolute'} right={showSidePanel ? '5px' : ''} top={'5px'} zIndex={1}>
                {showSidePanel ? (
                  <ShowIconComponent onClick={onTogglePanel} />
                ) : (
                  <HideIconComponent onClick={onTogglePanel} />
                )}
              </Box>
            )}
            {showSidePanel && (
              <Box
                width={{ base: '350px', '8xl': '450px !important' }}
                minW={{ base: '350px', '8xl': '450px !important' }}
              >
                <DriversSection refetchRequest={refetchRequest} />
              </Box>
            )}
          </Box>
          {showInputPage && (
            <Box
              minW={{ base: '350px', '8xl': '450px !important' }}
              ref={reference}
              background={'linear-gradient(180deg, #776ED0 0%, #776ED0 100%)'}
              transition={'.5s width ease-in-out'}
              width={showInputPage && showPlanPage ? '350px' : 'calc(100% - 350px)'}
            >
              <InputSection refetchRequest={refetchRequest} />
            </Box>
          )}
          {/* Plan Page */}
          {showPlanPage && (
            <Box width={'100%'}>
              {showInputPage ? (
                <motion.div
                  initial={{ opacity: 0, x: -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 1 }}
                >
                  <PlanSection refetchRequest={refetchRequest} />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 1 }}
                >
                  <PlanSection refetchRequest={refetchRequest} />
                </motion.div>
              )}
            </Box>
          )}
        </Flex>
      ) : (
        <Flex
          width={'fill-available'}
          className='scroll'
          h={`calc(100vh - ${HEADER_HEIGHT_WITH_NAVIGATION})`}
          justifyContent='space-around'
        >
          <PlanSection refetchRequest={refetchRequest} />
        </Flex>
      )}
      <AuthModal isOpen={isAuthModalOpen} onClose={onToggleAuthModal} />
    </Box>
  );
};

export default PlanPageScenario;
